import { useEffect, useRef } from "react";

/**
 * Prevents a focused `<input type="number">` from changing its value when the
 * user scrolls the mouse wheel / swipes over it. The value should only change
 * via the up/down arrows or by typing — not accidentally while scrolling.
 *
 * React registers `onWheel` as a passive listener, so `e.preventDefault()` from
 * a JSX `onWheel` handler is ignored. We therefore attach a native non-passive
 * wheel listener directly on the input element. We only block the event while
 * the field is focused, so scrolling the page over an unfocused field still
 * scrolls the page as usual.
 *
 * Pass the returned ref to MUI's `inputRef` prop (it points at the real <input>).
 */
export function useNoNumberScroll<T extends HTMLElement = HTMLInputElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (document.activeElement === el) {
        e.preventDefault();
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  return ref;
}
